import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ChevronLeft } from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import InstallAppHint from "@/components/InstallAppHint";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignInStudent(slug, plan) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  let url = `${backend}/api/auth/google/login?intent=student&academy=${encodeURIComponent(slug)}`;
  if (plan) url += `&plan=${encodeURIComponent(plan.name)}&price=${plan.price}`;
  window.location.href = url;
}

/** Per-academy student signup link (getrolloss.app/join/{slug}) — the ONLY
 * way a brand-new student can create an account tied to a specific academy.
 * A bare /student/login visit with no slug gets blocked server-side. */
export default function StudentJoin() {
  const { user, loading } = useAuth();
  const { slug } = useParams();
  const [academy, setAcademy] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    api.get(`/public/join/${slug}`)
      .then(r => { setAcademy(r.data); setSelectedPlan((r.data.academy_presets || [])[0] || null); })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (loading) return <div style={{minHeight:"100vh",display:"grid",placeItems:"center",background:"var(--bg)"}}><div className="label">Loading…</div></div>;
  if (user) return <Navigate to="/student/home" replace />;

  const presets = academy?.academy_presets || [];

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
      <div className="mx-auto flex flex-col" style={{ maxWidth: 460, minHeight: "100vh", borderLeft: "1px solid var(--border)", borderRight: "1px solid var(--border)" }}>
        <div className="p-5">
          <Link to="/" className="inline-flex items-center gap-1.5 label-tiny hover:text-white" data-testid="back-home">
            <ChevronLeft size={14} /> getrolloss.app
          </Link>
        </div>
        <div className="flex-1 flex flex-col justify-center px-7 pb-16">
          <LogoLockup size={32} beltWidth={260} />
          <div className="mt-12">
            {notFound && (
              <div data-testid="join-not-found">
                <div className="label" style={{ color: "var(--danger)" }}>Link not found</div>
                <h1 className="text-[28px] font-bold uppercase tracking-tight mt-2 leading-tight">
                  We couldn't find that academy.
                </h1>
                <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--text-dim)" }}>
                  Double-check the link your academy gave you, or ask them for their invite link or QR code again.
                </p>
              </div>
            )}
            {academy && (
              <div data-testid="join-academy-found">
                <div className="label" style={{ color: "var(--green)" }}>Student app</div>
                <h1 className="text-[30px] font-bold uppercase tracking-tight mt-2 leading-tight" data-testid="join-academy-name">
                  Join {academy.name}<br />on RollOSS.
                </h1>
                <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--text-dim)" }}>
                  Reserve classes, track your training streak, and watch your belt progression — all from your phone.
                </p>
                {presets.length > 0 && (
                  <label className="block label mt-8">Which discipline are you joining?
                    <select
                      className="input mt-1"
                      value={selectedPlan?.name || ""}
                      onChange={(e) => setSelectedPlan(presets.find(p => p.name === e.target.value) || null)}
                      data-testid="join-plan-select"
                    >
                      {presets.map(p => <option key={p.name} value={p.name}>{p.name} — ${p.price}/mo</option>)}
                    </select>
                  </label>
                )}
                <button onClick={() => googleSignInStudent(slug, selectedPlan)} className="btn btn-primary w-full mt-10" data-testid="join-google-signin">
                  Continue with Google <ArrowRight size={14}/>
                </button>
                <InstallAppHint />
                <div className="label-tiny text-center mt-5">
                  By continuing you agree to RollOSS&apos;s <Link to="/terms" className="underline" data-testid="link-terms">Terms</Link> and <Link to="/privacy" className="underline" data-testid="link-privacy">Privacy Policy</Link>, plus {academy.name}&apos;s training policy.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
